import React, { useState, useEffect, useCallback } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import { XMarkIcon, PlusIcon, PencilIcon, CheckIcon, BuildingOfficeIcon } from '@heroicons/react/20/solid';
import { useFranchisee } from '../context/FranchiseeContext';
import axios from 'axios';
import Swal from 'sweetalert2';

function MultipleFranchiseeDropdown({ user, onUpdate }) {
  const [query, setQuery] = useState('');
  const [selectedFranchisees, setSelectedFranchisees] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingFranchisee, setEditingFranchisee] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFranchiseeToAdd, setSelectedFranchiseeToAdd] = useState(null);
  const [newHubspotID, setNewHubspotID] = useState('');
  const [newExternalEmail, setNewExternalEmail] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const [updateDebounce, setUpdateDebounce] = useState(null);
  const { franchisees, refetchFranchisees } = useFranchisee();

  // Email validation helper
  const validateEmail = (email) => {
    if (!email) return true; // Empty is valid (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Initialize selected franchisees from user data
  useEffect(() => {
    // PRIORITY FIX: Use franchiseeAssociations first (has RC credentials), fallback to franchisees
    if (user?.franchiseeAssociations) {
      // Transform franchiseeAssociations to franchisees format for the dropdown
      const transformedFranchisees = user.franchiseeAssociations.map(assoc => ({
        id: assoc.franchisee.id,
        name: assoc.franchisee.name,
        owner: assoc.franchisee.owner,
        owner_email: assoc.franchisee.owner_email,
        assoc_client_type: assoc.franchisee.assoc_client_type,
        UserFranchiseeAssociation: {
          hubspotID: assoc.hubspotID,
          external_email: assoc.external_email
        },
        ringCentralCredential: assoc.ringCentralCredential || null
      }));
      
      setSelectedFranchisees(transformedFranchisees);
    } else if (user?.franchisees) {
      // Fallback to old format if franchiseeAssociations not available
      setSelectedFranchisees(user.franchisees);
    }
  }, [user]);

  const filteredFranchisees = franchisees.filter((franchisee) => {
    const matchesQuery = franchisee.name.toLowerCase().includes(query.toLowerCase());
    const isNotDeleted = franchisee.deletedAt === null;
    const isNotAlreadySelected = !selectedFranchisees.some(selected => selected.id === franchisee.id);
    return matchesQuery && isNotDeleted && isNotAlreadySelected;
  });

  const handleAddFranchisee = async (franchisee) => {
    // Show the add form instead of immediately adding
    setSelectedFranchiseeToAdd(franchisee);
    setShowAddForm(true);
    setIsOpen(false);
    setQuery('');
  };

  const handleConfirmAddFranchisee = async () => {
    if (!selectedFranchiseeToAdd) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      // Prepare data for the new association
      const franchiseeIds = [...selectedFranchisees.map(f => f.id), selectedFranchiseeToAdd.id];
      const hubspotIDs = [...selectedFranchisees.map(f => f.UserFranchiseeAssociation?.hubspotID || ''), newHubspotID];
      const externalEmails = [...selectedFranchisees.map(f => f.UserFranchiseeAssociation?.external_email || ''), newExternalEmail];

      console.log('Adding franchisee with data:', {
        franchiseeIds,
        hubspotIDs,
        externalEmails,
        newFranchisee: selectedFranchiseeToAdd
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/user/${user.id}/franchisee-associations`,
        {
          franchiseeIds,
          hubspotIDs,
          externalEmails
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      const newFranchisee = {
        ...selectedFranchiseeToAdd,
        UserFranchiseeAssociation: {
          hubspotID: newHubspotID,
          external_email: newExternalEmail
        }
      };
      setSelectedFranchisees([...selectedFranchisees, newFranchisee]);
      
      // Notify parent component
      if (onUpdate) {
        onUpdate(response.data.user);
      }

      // Refresh franchisee list
      await refetchFranchisees();
      
      Swal.fire({
        title: 'Success!',
        text: `${selectedFranchiseeToAdd.name} has been added to ${user.email}`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      // Reset form
      setShowAddForm(false);
      setSelectedFranchiseeToAdd(null);
      setNewHubspotID('');
      setNewExternalEmail('');

    } catch (error) {
      console.error('Error adding franchisee:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to add franchisee association',
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelAddFranchisee = () => {
    setShowAddForm(false);
    setSelectedFranchiseeToAdd(null);
    setNewHubspotID('');
    setNewExternalEmail('');
  };

  const handleRemoveFranchisee = async (franchiseeToRemove) => {
    const result = await Swal.fire({
      title: 'Remove Franchisee?',
      text: `Are you sure you want to remove ${franchiseeToRemove.name} from ${user.email}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        
        const response = await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/user/${user.id}/franchisee-associations`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: { franchiseeIds: [franchiseeToRemove.id] }
          }
        );

        // Update local state
        setSelectedFranchisees(selectedFranchisees.filter(f => f.id !== franchiseeToRemove.id));
        
        // Notify parent component
        if (onUpdate) {
          onUpdate(response.data.user);
        }

        // Refresh franchisee list
        await refetchFranchisees();
        
        Swal.fire({
          title: 'Removed!',
          text: `${franchiseeToRemove.name} has been removed from ${user.email}`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

      } catch (error) {
        console.error('Error removing franchisee:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to remove franchisee association',
          icon: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Debounced update function
  const handleUpdateAssociation = useCallback((franchisee, field, value) => {
    // Validate email if it's an email field
    if (field === 'external_email') {
      const isValid = validateEmail(value);
      setEmailValid(isValid);
      if (!isValid && value) return; // Don't update if invalid
    }

    // Update local state immediately for responsive UI
    setSelectedFranchisees(prev => prev.map(f => {
      if (f.id === franchisee.id) {
        return {
          ...f,
          UserFranchiseeAssociation: {
            ...f.UserFranchiseeAssociation,
            [field]: value
          }
        };
      }
      return f;
    }));

    // Clear existing debounce timer
    if (updateDebounce) {
      clearTimeout(updateDebounce);
    }

    // Set new debounce timer
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        
        // Get current state for the API call
        const franchiseeIds = selectedFranchisees.map(f => f.id);
        const hubspotIDs = selectedFranchisees.map(f => {
          if (f.id === franchisee.id) {
            return field === 'hubspotID' ? value : f.UserFranchiseeAssociation?.hubspotID || '';
          }
          return f.UserFranchiseeAssociation?.hubspotID || '';
        });
        const externalEmails = selectedFranchisees.map(f => {
          if (f.id === franchisee.id) {
            return field === 'external_email' ? value : f.UserFranchiseeAssociation?.external_email || '';
          }
          return f.UserFranchiseeAssociation?.external_email || '';
        });

        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/user/${user.id}/franchisee-associations`,
          {
            franchiseeIds,
            hubspotIDs,
            externalEmails
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Notify parent component
        if (onUpdate) {
          onUpdate(response.data.user);
        }

      } catch (error) {
        console.error('Error updating association:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to update franchisee association',
          icon: 'error',
          confirmButtonColor: '#3B82F6',
        });
      } finally {
        setIsLoading(false);
      }
    }, 800); // Wait 800ms after user stops typing

    setUpdateDebounce(timer);
  }, [selectedFranchisees, updateDebounce, onUpdate, user.id]);

  const handleEditClick = (franchisee) => {
    setEditingFranchisee(editingFranchisee?.id === franchisee.id ? null : franchisee);
  };

  return (
    <div className="w-full max-w-2xl">
      {/* Selected Franchisees Display */}
      <div className="space-y-2 mb-3">
        {selectedFranchisees.length === 0 ? (
          <div className="flex items-center gap-2 text-gray-500 text-xs italic py-2 px-3 bg-gray-700 rounded-lg border border-gray-600">
            <BuildingOfficeIcon className="h-4 w-4 text-gray-600" />
            <span>No franchisees assigned</span>
          </div>
        ) : (
          selectedFranchisees.map((franchisee) => (
            <div 
              key={franchisee.id} 
              className="bg-gray-700 rounded-lg border border-gray-600 p-3 text-xs transition-all duration-200 hover:border-gray-500 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
                    <span className="font-semibold text-white truncate">
                      {franchisee.name}
                    </span>
                  </div>
                  <div className="space-y-1 pl-6">
                    <div className="flex items-center gap-2 text-gray-300">
                      <span className="text-gray-400 font-medium w-16">HubSpot:</span>
                      <span className="text-blue-300 font-mono text-xs">
                        {franchisee.UserFranchiseeAssociation?.hubspotID || (
                          <span className="text-gray-500 italic font-sans">Not set</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <span className="text-gray-400 font-medium w-16">Email:</span>
                      <span className="text-green-300 truncate">
                        {franchisee.UserFranchiseeAssociation?.external_email || (
                          <span className="text-gray-500 italic">Not set</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <span className="text-gray-400 font-medium w-16">RC Ext:</span>
                      {franchisee.ringCentralCredential && franchisee.ringCentralCredential.extensionID ? (
                        <span className="text-cyan-300 font-mono text-xs">
                          {franchisee.ringCentralCredential.extensionID}
                        </span>
                      ) : (
                        <span className="text-gray-500 italic font-sans">Not set</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEditClick(franchisee)}
                    disabled={isLoading}
                    className={`p-1.5 rounded transition-colors duration-150 ${
                      editingFranchisee?.id === franchisee.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-blue-400 hover:bg-gray-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={editingFranchisee?.id === franchisee.id ? 'Close editor' : 'Edit details'}
                  >
                    {editingFranchisee?.id === franchisee.id ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <PencilIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleRemoveFranchisee(franchisee)}
                    disabled={isLoading}
                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Remove franchisee"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Expandable Edit Form with Animation */}
              {editingFranchisee?.id === franchisee.id && (
                <div className="mt-3 pt-3 border-t border-gray-600 space-y-3 animate-fadeIn">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      HubSpot ID
                    </label>
                    <input
                      type="text"
                      value={franchisee.UserFranchiseeAssociation?.hubspotID || ''}
                      onChange={(e) => handleUpdateAssociation(franchisee, 'hubspotID', e.target.value)}
                      disabled={isLoading}
                      className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      placeholder="e.g., 23421412"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      External Email
                    </label>
                    <input
                      type="email"
                      value={franchisee.UserFranchiseeAssociation?.external_email || ''}
                      onChange={(e) => {
                        setEmailValid(validateEmail(e.target.value));
                        handleUpdateAssociation(franchisee, 'external_email', e.target.value);
                      }}
                      disabled={isLoading}
                      className={`w-full px-3 py-2 text-sm bg-gray-800 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                        !emailValid && franchisee.UserFranchiseeAssociation?.external_email
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-600 focus:ring-blue-500'
                      }`}
                      placeholder="e.g., user@example.com"
                    />
                    {!emailValid && franchisee.UserFranchiseeAssociation?.external_email && (
                      <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                        <span>⚠</span>
                        <span>Invalid email format</span>
                      </p>
                    )}
                  </div>
                  {isLoading && (
                    <div className="flex items-center gap-2 text-blue-400 text-xs">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-400"></div>
                      <span>Saving changes...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Franchisee Form */}
      {showAddForm && selectedFranchiseeToAdd && (
        <div className="mb-3 p-4 bg-gradient-to-br from-blue-900/50 to-blue-800/50 border border-blue-600 rounded-lg shadow-lg animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-5 w-5 text-blue-400" />
              <h4 className="text-sm font-semibold text-white">
                Add {selectedFranchiseeToAdd.name}
              </h4>
            </div>
            <button
              onClick={handleCancelAddFranchisee}
              disabled={isLoading}
              className="text-blue-300 hover:text-white transition-colors disabled:opacity-50"
              title="Cancel"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-blue-200 mb-1.5">
                HubSpot ID <span className="text-blue-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={newHubspotID}
                onChange={(e) => setNewHubspotID(e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-blue-500 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                placeholder="e.g., 23421412"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-200 mb-1.5">
                External Email <span className="text-blue-400 font-normal">(optional)</span>
              </label>
              <input
                type="email"
                value={newExternalEmail}
                onChange={(e) => {
                  setNewExternalEmail(e.target.value);
                  setEmailValid(validateEmail(e.target.value));
                }}
                disabled={isLoading}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                  !emailValid && newExternalEmail
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-blue-500 focus:ring-blue-400'
                }`}
                placeholder="e.g., user@example.com"
              />
              {!emailValid && newExternalEmail && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <span>⚠</span>
                  <span>Please enter a valid email address</span>
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleConfirmAddFranchisee}
                disabled={isLoading || (!emailValid && newExternalEmail)}
                className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <PlusIcon className="h-4 w-4" />
                    Add Franchisee
                  </span>
                )}
              </button>
              <button
                onClick={handleCancelAddFranchisee}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Franchisee Dropdown */}
      {!showAddForm && (
        <div className="relative">
          <Combobox value={null} onChange={handleAddFranchisee}>
            <div className="relative">
              <ComboboxInput
                className="w-full pl-10 pr-10 py-2.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-500 transition-all"
                placeholder="Search and add franchisee..."
                displayValue={() => query}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
              </div>
              <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-3">
                <PlusIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              </ComboboxButton>
            </div>

            {isOpen && (
              <ComboboxOptions className="absolute w-full mt-2 bg-gray-700 shadow-xl max-h-60 rounded-lg py-1 text-sm border border-gray-600 overflow-auto focus:outline-none z-50 animate-fadeIn">
                {filteredFranchisees.length === 0 ? (
                  <div className="px-4 py-3 text-gray-400 text-center">
                    {query ? (
                      <span>No franchisees found matching "{query}"</span>
                    ) : (
                      <span>No franchisees available to add</span>
                    )}
                  </div>
                ) : (
                  filteredFranchisees.map((franchisee) => (
                    <ComboboxOption
                      key={franchisee.id}
                      value={franchisee}
                      className={({ active }) =>
                        `cursor-pointer select-none relative py-2.5 px-4 transition-colors ${
                          active ? 'bg-blue-600 text-white' : 'text-gray-200 hover:bg-gray-600'
                        }`
                      }
                    >
                      {({ active }) => (
                        <div className="flex items-center gap-2">
                          <BuildingOfficeIcon className={`h-4 w-4 ${active ? 'text-blue-200' : 'text-gray-400'}`} />
                          <span className="block truncate font-medium">{franchisee.name}</span>
                        </div>
                      )}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            )}
          </Combobox>
        </div>
      )}
    </div>
  );
}

export default MultipleFranchiseeDropdown;
